import { isSmallAccountMode } from "./smallAccountMode";

export const STRATEGY_LABELS = {
  wheel: "Wheel Strategy",
  tactical_spread: "Tactical Spread",
  regime_spread: "Regime Spread",
  earnings: "Earnings Strategy",
};

export function formatStrategyLabel(strategy) {
  return STRATEGY_LABELS[strategy] || "Strategy";
}

export function buildSimulationLaunchRequest(decision, symbol, portfolioValue, buyingPower, strategy, accountSource) {
  const meta = decision?.meta || {};
  const rawStrategy = strategy || meta?.strategy;
  if (!rawStrategy) return null;

  const effectiveCapital = Number(
    buyingPower ?? portfolioValue ?? meta?.buying_power ?? meta?.portfolio_value ?? 2500
  );
  const capital = Number.isFinite(effectiveCapital) && effectiveCapital > 0 ? effectiveCapital : 2500;
  const dte = Number(meta?.dte || meta?.days_to_earnings || 60);
  const clampedDays = Math.max(30, Math.min(180, dte || 60));
  const preset = isSmallAccountMode(capital) ? "small_account" : "balanced";
  const baseLaunch = {
    symbol,
    initial_capital: capital,
    portfolio_value: capital,
    buying_power: capital,
    account_source: accountSource || "trader",
    previewStrategy: rawStrategy,
    previewMeta: { ...meta },
    previewDecision: {
      action: decision?.action || null,
      should_trade: Boolean(decision?.should_trade),
      price: decision?.price ?? null,
      reason: decision?.reason ?? null,
    },
    days: clampedDays,
    preset,
  };

  if (rawStrategy === "earnings") {
    return {
      ...baseLaunch,
      mode: "earnings",
      sim_mode: meta?.sim_mode || null,
      earningsDirection: meta?.opportunity_strategy || null,
      earningsDate: meta?.earnings_date || null,
    };
  }

  const isTactical = ["tactical_spread", "tactical_spreads", "tactical_debit_spread"].includes(rawStrategy);
  if (isTactical) {
    return {
      ...baseLaunch,
      mode: "tactical_spreads",
    };
  }

  const isRegime = ["regime_spread", "regime_spreads", "regime_debit_spread"].includes(rawStrategy);
  if (isRegime) {
    return {
      ...baseLaunch,
      mode: "regime_spreads",
      days: Math.max(60, Math.min(180, dte || 90)),
    };
  }

  return {
    ...baseLaunch,
    mode: "wheel",
    days: Math.max(30, Math.min(180, dte ? dte * 4 : 90)),
  };
}

export function resolveSimulationMode(override, mode) {
  return override?.mode || mode;
}

export function buildSimulationPayload({
  override = null,
  mode,
  capital,
  days,
  preset,
  selectedOpp = null,
}) {
  const effectiveMode = resolveSimulationMode(override, mode);
  let simMode = effectiveMode;
  if (effectiveMode === "earnings") {
    simMode = override?.sim_mode || selectedOpp?.sim_mode || "tactical_spreads";
  }

  const payload = {
    mode: simMode,
    initial_capital: Number(override?.initial_capital ?? capital),
    portfolio_value: Number(
      override?.portfolio_value
      ?? override?.buying_power
      ?? override?.initial_capital
      ?? capital
    ),
    days: Number(override?.days ?? days),
    n_simulations: 300,
  };
  if (effectiveMode === "wheel") {
    payload.preset = override?.preset || preset;
  }
  if (override?.previewStrategy) {
    payload.previewStrategy = override.previewStrategy;
  }
  if (override?.previewMeta) {
    payload.previewMeta = override.previewMeta;
  }
  if (override?.previewDecision) {
    payload.previewDecision = override.previewDecision;
  }

  return { effectiveMode, simMode, payload };
}
